import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useCreateSolution } from "@/hooks/use-solutions";
import { cn } from "@/lib/utils";

type Stage = "sales" | "discovery" | "build";

const STAGES: ReadonlyArray<{ id: Stage; label: string; sub: string }> = [
  { id: "sales", label: "Pre-sale", sub: "Proposal & SOW intake" },
  { id: "discovery", label: "Discovery", sub: "Active scope decisions" },
  { id: "build", label: "Live build", sub: "Change requests after launch" },
];

interface NewSolutionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewSolutionSheet({ open, onOpenChange }: NewSolutionSheetProps) {
  const create = useCreateSolution();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [summary, setSummary] = useState("");
  const [stage, setStage] = useState<Stage>("discovery");

  useEffect(() => {
    if (open) {
      setName("");
      setClientName("");
      setSummary("");
      setStage("discovery");
    }
  }, [open]);

  function submit() {
    if (!name.trim() || !clientName.trim()) return;
    create.mutate(
      {
        name: name.trim(),
        clientName: clientName.trim(),
        summary: summary.trim() || null,
        stage,
      },
      {
        onSuccess: (s) => {
          toast.success(`Created ${s.name}`);
          onOpenChange(false);
          // Land on Scope — every new solution needs a baseline uploaded
          // before Process AI is usable, so this is the most useful first stop.
          navigate({
            to: "/s/$solutionId/scope",
            params: { solutionId: s.id },
            search: { view: "list" },
          });
        },
      },
    );
  }

  const canSubmit = name.trim().length > 0 && clientName.trim().length > 0 && !create.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] flex flex-col">
        <SheetHeader className="border-b border-[var(--color-border)]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            New solution
          </div>
          <SheetTitle className="font-display text-lg leading-snug mt-1">
            Start an engagement
          </SheetTitle>
          <SheetDescription className="sr-only">Create a new scoping engagement.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <Field label="Client" required>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Acme Cold Storage"
              className="h-9 text-sm"
              autoFocus
            />
          </Field>

          <Field label="Solution name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Warehouse Management"
              className="h-9 text-sm"
            />
          </Field>

          <Field label="Phase">
            <div className="grid grid-cols-3 gap-2">
              {STAGES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStage(s.id)}
                  className={cn(
                    "rounded-lg border-2 px-3 py-2.5 text-left transition-all",
                    stage === s.id
                      ? "border-nymbl-navy bg-nymbl-sky/30"
                      : "border-[var(--color-border)] hover:border-nymbl-blue",
                  )}
                >
                  <div className="font-display font-semibold text-sm leading-tight">{s.label}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                    {s.sub}
                  </div>
                </button>
              ))}
            </div>
          </Field>

          <Field label="Summary" help="Optional — one-liner you'll see on the solutions card">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="text-sm"
              placeholder="What problem are we solving in this engagement?"
            />
          </Field>
        </div>

        <SheetFooter className="border-t border-[var(--color-border)] flex flex-row sm:justify-end gap-2 p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {create.isPending ? "Creating…" : "Create solution"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  required,
  help,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </div>
      {children}
      {help && <p className="text-[11px] text-muted-foreground mt-1">{help}</p>}
    </div>
  );
}
